import type { SampleRow } from "../../server/core/samples";
import type { ScreenContext } from "../shell/types";
import { api, el, toast } from "../ui/dom";
import { icon } from "../ui/icons";
import { firstPageParams, list, type Column } from "../ui/list";
import { openModal } from "../ui/modal";
import { stageStrip } from "../ui/stage";
import { pageHead } from "./empty";
import { askPatients, patientList } from "./patients/patients";

// Each module's Overview (brief 1A-6, mockup): its stage strip, then its list by stage with the
// mockup's columns. No module stores records yet, so in production the list is empty. On deploy
// previews and local runs the server hands back fictional sample rows so the kit can be walked;
// a sample row opens in a modal, and bulk actions say they come with the module.

type Step = [title: string, line: string, screen: string];

interface OverviewDef {
  line: string;
  flows: { title: string; lane?: string; steps: Step[] }[];
  list: {
    title: string;
    noun: string;
    /** The screen the list belongs to; the list shows only to people who can open it. */
    screen: string;
    all: string;
    columns: string[];
    stages: readonly string[];
  };
}

// Stages, columns and strips as in docs/design/mockup.html (ST, BCOLS, flowCard, APST).
const PLACEMENT = [
  "Matched",
  "Submitted",
  "Client review",
  "Interview",
  "Offer & wage",
  "Credentialing",
  "Cleared",
  "Working",
];
const EPISODE = [
  "Referral",
  "Intake",
  "Authorization",
  "RN assessment",
  "Admission",
  "Care plan",
  "Scheduling",
  "In care",
  "Recertification",
  "Discharge",
];
const TRIP = [
  "Requested",
  "Authorized",
  "Scheduled",
  "Dispatched",
  "Picked up",
  "Completed",
  "Billed",
  "Paid",
];
const APPLICANT = ["New", "Activated", "Documents started", "Engaged", "Moved to Staffing"];

const OVERVIEWS: Record<string, OverviewDef> = {
  staffing: {
    line: "Start with a client. Clients give requisitions, candidates are paired to them, and placements work, bill and get paid.",
    flows: [
      {
        title: "The staffing flow",
        steps: [
          ["1 Client", "contract, insurance, requirements", "staffing/clients"],
          ["2 Requisition", "open, layered requirements", "staffing/requisitions"],
          ["3 Candidate", "intake, mini-profile, bench", "staffing/candidates"],
          ["4 Placement", "submit → credential → start", "staffing/placements"],
          ["5 Time", "weekly timesheets", "staffing/time"],
          ["6 Bill → deposit", "invoice or reverse invoice", "staffing/billing"],
        ],
      },
    ],
    list: {
      title: "Placements by stage",
      noun: "placements",
      screen: "placements",
      all: "All placements",
      columns: ["Worker", "Requisition", "Client"],
      stages: PLACEMENT,
    },
  },
  homecare: {
    line: "Each client's setup decides how referrals are taken, authorizations tracked, and visits documented and billed.",
    flows: [
      {
        title: "Home Care flow · client setup",
        steps: [
          ["1 Client", "type, contract, rates", "homecare/clients"],
          ["2 Rules", "authorizations, care rules", "homecare/clients"],
          ["3 Billing setup", "format, codes, terms", "homecare/billing"],
        ],
      },
      {
        title: "Home Care flow · each patient",
        lane: "Each step hands off to the next. Caregivers are hired in Staffing.",
        steps: [
          ["1 Intake", "referral, payer", "homecare/patients"],
          ["2 Assessment", "RN or consultation", "homecare/patients"],
          ["3 Care plan", "admit, plan, schedule", "homecare/patients"],
          ["4 Visits", "check in, tasks, out", "homecare/visits"],
          ["5 Bill", "weekly", "homecare/billing"],
          ["6 Paid", "remittance matched", "homecare/payments"],
        ],
      },
    ],
    list: {
      title: "Patients by stage",
      noun: "patients",
      screen: "patients",
      all: "All patients",
      columns: ["Patient", "Client type", "Referral"],
      stages: EPISODE,
    },
  },
  transport: {
    line: "Non-emergency medical transportation (NEMT) with wheelchair transport. Each direction is its own leg.",
    flows: [
      {
        title: "Transportation flow",
        steps: [
          ["1 Client", "rates, wait, billing", "transport/clients"],
          ["2 Request", "rider, pickup, appointment", "transport/trips"],
          ["3 Authorize", "PO or authorization", "transport/trips"],
          ["4 Schedule", "driver, van, leave-by", "transport/trips"],
          ["5 Trip", "en route → drop-off", "transport/trips"],
          ["6 Bill → paid", "per leg or monthly", "transport/billing"],
        ],
      },
    ],
    list: {
      title: "Legs by stage",
      noun: "legs",
      screen: "trips",
      all: "All trips",
      columns: ["Rider", "Leg", "When"],
      stages: TRIP,
    },
  },
  jobs: {
    line: "Jobs from VMS feeds and requisitions, shown with pay. Applicants are leads until they move to Staffing.",
    flows: [
      {
        title: "Job board flow",
        steps: [
          ["1 Posted", "from VMS feeds", "jobs/postings"],
          ["2 Viewed", "with pay rate", "jobs/postings"],
          ["3 Applied", "quick apply", "jobs/applicants"],
          ["4 Activated", "code by text", "jobs/applicants"],
          ["5 To Staffing", "recruiter assigned", "staffing/candidates"],
        ],
      },
    ],
    list: {
      title: "Moving the needle",
      noun: "applicants",
      screen: "applicants",
      all: "All applicants",
      columns: ["Applicant", "Score"],
      stages: APPLICANT,
    },
  },
};

/** Bulk actions on a list of people, as in the mockup's bulk bar. */
const BULK: [string, string][] = [
  ["Text", "device-mobile"],
  ["Email", "mail"],
  ["Request documents", "file"],
  ["Assign", "person"],
  ["Move stage", "workflow"],
  ["Export", "download"],
];

/** The mockup's stage chip: the first stage plain, the last done, the rest in progress. */
function stageChip(stages: readonly string[], i: number): HTMLElement {
  const kind = i === stages.length - 1 ? "ok" : i === 0 ? "plain" : "info";
  return el("span", { class: `chip ${kind}` }, stages[i] ?? "");
}

export function hasOverview(section: string): boolean {
  return section in OVERVIEWS;
}

export async function overviewScreen(host: HTMLElement, ctx: ScreenContext) {
  const def = OVERVIEWS[ctx.section.id];
  if (!def) return;
  const canOpen = (path: string) => {
    const [sec, scr] = path.split("/");
    return ctx.me.menus.some((m) => m.id === sec && m.screens.some((s) => s.id === scr));
  };
  const showList = canOpen(`${ctx.section.id}/${def.list.screen}`);
  // Home Care's list is its real patients (brief 1C-1), not samples.
  if (ctx.section.id === "homecare") return homecareOverview(host, ctx, def, showList, canOpen);
  const res = showList
    ? await api<{ rows: SampleRow[]; flow: number[][] | null }>(
        "GET",
        `/api/samples/${ctx.section.id}`,
      )
    : null;
  const rows = res?.ok ? res.body.rows : [];
  const flow = res?.ok ? res.body.flow : null;

  host.append(pageHead(ctx, def.line));
  def.flows.forEach((f, fi) =>
    host.append(
      stageStrip({
        title: f.title,
        lane: f.lane,
        steps: f.steps.map(([title, line, path], si) => ({
          title,
          line,
          count: flow?.[fi]?.[si]?.toLocaleString("en-US") ?? 0,
          go: canOpen(path) ? () => ctx.go(`/${path}`) : undefined,
        })),
      }),
    ),
  );
  if (!showList) return;

  const L = def.list;
  const stageOf = (r: SampleRow) => L.stages[r.stage] ?? "";
  const columns: Column<SampleRow>[] = L.columns.map((label, i) => ({
    id: `c${i}`,
    label,
    text: (r) => r.cells[i] ?? "",
    cell:
      i === 0
        ? (r) =>
            el(
              "span",
              {},
              el("b", {}, r.cells[0] ?? ""),
              // The applicant list shows the title under the name (mockup "Moving the needle").
              ctx.section.id === "jobs"
                ? el("div", { class: "small muted" }, r.cells[1] ?? "")
                : null,
            )
        : undefined,
  }));
  if (ctx.section.id === "jobs") {
    // Applicant (name and title), then Score: the score is the third cell.
    columns[1] = { id: "c1", label: "Score", text: (r) => r.cells[2] ?? "", num: true };
  }
  columns.push({
    id: "stage",
    label: "Stage",
    text: stageOf,
    cell: (r) => stageChip(L.stages, r.stage),
    values: L.stages,
  });

  const open = (r: SampleRow) =>
    openModal({
      title: r.cells[0] ?? "",
      sub: "A sample row on this preview.",
      body: [
        el(
          "dl",
          { class: "kv" },
          ...columns.flatMap((c) => [
            el("dt", {}, c.label),
            el("dd", {}, c.id === "stage" ? stageChip(L.stages, r.stage) : c.text(r)),
          ]),
        ),
      ],
    });

  const allBtn = el("button", { type: "button", class: "btn sm" }, L.all);
  allBtn.addEventListener("click", () => ctx.go(`/${ctx.section.id}/${L.screen}`));

  host.append(
    el(
      "section",
      { "aria-label": L.title },
      el("div", { class: "cardhead" }, el("h2", {}, L.title), allBtn),
      list<SampleRow>({
        id: `${ctx.section.id}/overview`,
        userId: ctx.me.user.id,
        noun: L.noun,
        rows,
        rowId: (r) => r.id,
        columns,
        chips: { values: L.stages, of: stageOf },
        board: {
          stages: L.stages,
          stageOf,
          tile: (r) =>
            ctx.section.id === "jobs"
              ? [r.cells[0] ?? "", r.cells[1] ?? "", `Score ${r.cells[2] ?? ""}`]
              : r.cells.slice(0, 3),
        },
        bulk: BULK.map(([label, ic]) => ({
          label,
          icon: ic,
          run: (picked) => toast(`${label}: ${picked.length} selected`, "Not available yet"),
        })),
        onOpen: open,
      }),
    ),
  );
}

/**
 * Home Care's Overview (brief 1C-1, mockup): the two flow strips, Patients by stage from the real
 * patients, and Open newest referral.
 */
async function homecareOverview(
  host: HTMLElement,
  ctx: ScreenContext,
  def: OverviewDef,
  showList: boolean,
  canOpen: (path: string) => boolean,
) {
  const res = showList
    ? await askPatients(firstPageParams("homecare/overview", ctx.me.user.id))
    : null;
  const data = res?.ok ? res.body : null;
  const newest = data?.newest;
  const open = el(
    "button",
    { type: "button", class: "btn pri" },
    icon("eye"),
    " Open newest referral",
  );
  open.addEventListener("click", () => newest && ctx.go(`/homecare/patients/${newest}`));
  host.append(pageHead(ctx, def.line, newest ? [open] : []));
  const before = data?.inIntake ?? 0;
  def.flows.forEach((f, fi) =>
    host.append(
      stageStrip({
        title: f.title,
        lane: f.lane,
        steps: f.steps.map(([title, line, path], si) => ({
          title,
          line,
          // The first patient step counts referrals still in intake; the rest come with their briefs.
          count: fi === 1 && si === 0 ? before : 0,
          go: canOpen(path) ? () => ctx.go(`/${path}`) : undefined,
        })),
      }),
    ),
  );
  if (!data) return;
  const allBtn = el("button", { type: "button", class: "btn sm" }, def.list.all);
  allBtn.addEventListener("click", () => ctx.go("/homecare/patients"));
  host.append(
    el(
      "section",
      { "aria-label": def.list.title },
      el("div", { class: "cardhead" }, el("h2", {}, def.list.title), allBtn),
      patientList(ctx, data, "homecare/overview"),
    ),
  );
}
